import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase, Product } from "@/lib/supabase";
import ProductDetailModal from "@/components/feature/ProductDetailModal";
import ImageCarousel from "@/components/base/ImageCarousel";
import PriceDisplay from "@/components/base/PriceDisplay";

interface FeaturedProductsProps {
  limit?: number;
  showViewAll?: boolean;
}

const FeaturedProducts = ({ limit = 6, showViewAll = true }: FeaturedProductsProps) => {
  const [featured, setFeatured] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    supabase
      .from("products")
      .select("*")
      .eq("is_featured", true)
      .order("created_at", { ascending: false })
      .limit(limit)
      .then(({ data }) => {
        setFeatured(data || []);
        setLoading(false);
      });
  }, [limit]);

  const mappedProduct = selectedProduct
    ? {
        id: selectedProduct.id,
        title: selectedProduct.title,
        description: selectedProduct.description,
        long_description: selectedProduct.long_description || null,
        highlights: selectedProduct.highlights || null,
        price: selectedProduct.price,
        discounted_price: selectedProduct.discounted_price || null,
        image: selectedProduct.image,
        images: selectedProduct.images || [],
        badge: selectedProduct.badge || undefined,
        category: selectedProduct.category,
        purchaseOptions: selectedProduct.purchase_options || [],
      }
    : null;

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-[#fdf8f8] to-white px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]">
              Handpicked for You
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Featured Products
            </h2>
          </div>
          {showViewAll && (
            <Link to="/products" className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] whitespace-nowrap flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity">
              View All Products
              <i className="ri-arrow-right-line text-[#F78FB3]" />
            </Link>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                <div className="h-64 bg-gray-100" />
                <div className="p-5 flex flex-col gap-3">
                  <div className="h-4 bg-gray-100 rounded-full w-3/4" />
                  <div className="h-3 bg-gray-100 rounded-full w-full" />
                  <div className="h-3 bg-gray-100 rounded-full w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-star-line text-4xl mb-3 block" />
            <p>No featured products yet. Mark products as featured in the admin panel.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((product) => {
              const displayImages = product.images?.length ? product.images : product.image ? [product.image] : [];
              return (
                <div key={product.id} className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1.5 transition-all duration-300">
                  <div className="relative h-64 overflow-hidden">
                    <ImageCarousel images={displayImages} alt={product.title} className="w-full h-full" showArrows showDots />
                    {product.badge && (
                      <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] z-20">
                        {product.badge}
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-gray-900 text-base line-clamp-1 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {product.title}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-2 mb-4 leading-relaxed">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <PriceDisplay price={product.price} discountedPrice={product.discounted_price} size="md" />
                      <button
                        onClick={() => setSelectedProduct(product)}
                        className="px-4 py-2 rounded-full text-sm font-semibold border-2 border-transparent bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
                      >
                        Buy Now
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ProductDetailModal product={mappedProduct} onClose={() => setSelectedProduct(null)} />
    </section>
  );
};

export default FeaturedProducts;
